import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../api';

export const fetchMeals = createAsyncThunk(
  'meals/fetchMeals',
  async ({ page = 1, searchTerm="", typeId="" }, { rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/meals-list?page=${page}`, 
        {
          searchTerm,
          'typeId':typeId
        });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch meals');
    }
  }
);

// Async thunk to delete a meal
export const deleteMeal = createAsyncThunk(
  'meals/deleteMeal',
  async (mealId, { dispatch, rejectWithValue }) => {
    try {
      await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/meals-delete`, { id: mealId });
      dispatch(fetchMeals({ page: 1 })); // Fetch updated meal list after deletion
      return mealId;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete meal');
    }
  }
);

// Async thunk to create a new meal
export const createMeal = createAsyncThunk(
  'meals/createMeal',
  async (mealData, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/meals`, mealData);
      if (response.data.success) {
        dispatch(fetchMeals({ page: 1 })); // Fetch updated meal list after creation
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create meal');
    }
  }
);

// Async thunk to update an existing meal
export const updateMeal = createAsyncThunk(
  'meals/updateMeal',
  async ({id,mealData}, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post(`${process.env.REACT_APP_API_BASE_URL}/admin/meals/`+id, mealData);
      if (response.data.success) {
        dispatch(fetchMeals({ page: mealData.page || 1 })); // Fetch updated meal list after update
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update meal');
    }
  }
);

const mealsSlice = createSlice({
  name: 'admin_meals',
  initialState: {
    meals: [],
    currentMealPage: 1,
    totalMealPages: 1,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    setMealPage: (state, action) => {
      state.currentMealPage = action.payload;
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMeals.fulfilled, (state, action) => {
        state.loading = false;
        state.meals = action.payload.data.data;
        state.currentMealPage = action.payload.data.current_page || 1;
        state.totalMealPages = action.payload.data.last_page || 0;
      })
      .addCase(fetchMeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteMeal.fulfilled, (state, action) => {
        state.meals = state.meals.filter(meal => meal.id !== action.payload);
      })
      .addCase(deleteMeal.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createMeal.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.successMessage = 'Meal created successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(createMeal.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      })
      .addCase(updateMeal.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.meals.findIndex(meal => meal.id === action.payload.data.id);
          if (index !== -1) {
            state.meals[index] = action.payload.data; // Update with new data
          }
          state.successMessage = 'Meal updated successfully';
        } else {
          state.error = action.payload.message; // Handle error from response
        }
      })
      .addCase(updateMeal.rejected, (state, action) => {
        state.error = action.payload.message; // Handle error on rejection
      });
  },
});

export const { setMealPage, clearMessages } = mealsSlice.actions;

export default mealsSlice.reducer;